import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useMotionValue, animate } from "framer-motion";
import { Flame, Salad, Dumbbell, Moon, Droplets, ChevronRight } from "lucide-react";
import { apiClient } from "../../services/api";

/**
 * FitScore Hero — mirrors the mobile app spec (Path A: server is source of truth).
 *
 * TWO CARDS stacked:
 *   1. Dark navy hero card: ring, status label, sub-label, streak pill, 5 component tiles
 *   2. White "Next Action" card: the single most-impactful next thing
 *
 * Score comes from the server (`GET /api/fitscore/today`) which uses the exact
 * same 30/30/20/10/10 breakdown as the mobile client's local `calculateFitScore`.
 */

const CARD_BG = "#0A1628";
const CARD_BORDER = "rgba(255,255,255,0.06)";

// Tile tints — from the mobile spec
const TINTS = {
  nutrition: "#00FFB3",   // green
  movement: "#00D4FF",    // teal
  recovery: "#A78BFA",    // violet
  hydration: "#F5C518",   // gold
  consistency: "#F5C518", // gold (piggybacks on the streak pill)
};

// Ring colour bands — from the mobile spec
function ringColour(score) {
  if (score >= 80) return "#00FFB3";
  if (score >= 60) return "#00D4FF";
  if (score >= 40) return "#F5C518";
  return "#E24B4A";
}

// Status label — right-side text
function statusLabel(total) {
  if (total >= 100) return "Perfect day";
  if (total >= 90) return "Almost perfect";
  if (total >= 60) return "On track";
  if (total >= 40) return "Good start today";
  if (total > 0) return "Just getting going";
  return "Let's get moving";
}

// Sub-label — what to do next, priority chain (matches spec section 6)
function subLabel(c) {
  const nutrition = c?.nutrition ?? 0;
  const movement = c?.movement ?? 0;
  const recovery = c?.recovery ?? c?.checkin ?? 0;
  const hydration = c?.hydration ?? 0;
  const consistency = c?.consistency ?? 0;
  const total = c?.total ?? 0;
  if (nutrition < 15) return "Start with a meal";
  if (movement === 0) return "Move your body today";
  if (recovery < 10) return "Log your check-in";
  if (hydration < 10) return "Drink some water";
  if (total >= 100) return "You nailed every component";
  if (consistency < 5) return "Keep the streak going";
  return "Keep stacking points";
}

// Next-action card — single most-impactful action (matches spec section 9)
function nextAction(c) {
  const nutrition = c?.nutrition ?? 0;
  const movement = c?.movement ?? 0;
  const hydration = c?.hydration ?? 0;
  const recovery = c?.recovery ?? c?.checkin ?? 0;
  if (nutrition < 15)  return { title: "Log your first meal", sub: "Nutrition · +2 FP", to: "/nutrition",           Icon: Salad,    tint: TINTS.nutrition };
  if (movement === 0)  return { title: "Start a workout",     sub: "Workout · +5 FP",   to: "/workouts",           Icon: Dumbbell, tint: TINTS.movement };
  if (hydration < 10)  return { title: "Log water",           sub: "Hydration · +1 FP", to: "/nutrition",          Icon: Droplets, tint: TINTS.hydration };
  if (recovery === 0)  return { title: "Do morning check-in", sub: "Recovery · +10 pts",to: "/home",               Icon: Moon,     tint: TINTS.recovery };
  return                        { title: "Perfect day in reach", sub: "Keep it up",        to: "/home",               Icon: Flame,    tint: TINTS.nutrition };
}

function AnimatedRing({ score }) {
  const mv = useMotionValue(0);
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const controls = animate(mv, score, {
      duration: 0.9,
      ease: [0.16, 1, 0.3, 1], // cubic ease-out
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return controls.stop;
  }, [score, mv]);

  const size = 112;
  const stroke = 4;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, display));
  const dashOffset = c * (1 - pct / 100);
  const colour = pct === 0 ? "#FFFFFF" : ringColour(pct);

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} data-testid="fitscore-ring">
      <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(255,255,255,0.18)" strokeWidth={stroke} fill="none" />
      <circle
        cx={size / 2} cy={size / 2} r={r}
        stroke={colour} strokeWidth={stroke} fill="none"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={dashOffset}
        style={{
          transform: "rotate(-90deg)",
          transformOrigin: "50% 50%",
          transition: "stroke 0.35s ease, stroke-dashoffset 0.9s cubic-bezier(0.16,1,0.3,1)",
        }}
      />
      <text x="50%" y="47%" textAnchor="middle" dominantBaseline="middle"
        style={{ fontFamily: "Barlow Condensed, sans-serif", fontWeight: 900, fontSize: 40, fill: "#FFFFFF" }}>
        {display}
      </text>
      <text x="50%" y="72%" textAnchor="middle"
        style={{ fontFamily: "Barlow, sans-serif", fontSize: 10, fill: "rgba(255,255,255,0.55)", letterSpacing: "0.14em", textTransform: "uppercase" }}>
        FITSCORE
      </text>
    </svg>
  );
}

function ComponentTile({ Icon, label, value, max, colour, testid, onClick }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      data-testid={testid}
      className="text-left"
      style={{ minWidth: 0, background: "transparent", border: 0, padding: 0, cursor: "pointer" }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
        <Icon size={12} color={colour} />
        <span style={{ fontFamily: "Barlow, sans-serif", fontSize: 12, fontWeight: 500, color: "rgba(255,255,255,0.75)" }}>
          {label}
        </span>
      </div>
      <div style={{ height: 3, background: "rgba(255,255,255,0.10)", borderRadius: 100, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: colour, transition: "width 0.7s cubic-bezier(0.16,1,0.3,1)" }} />
      </div>
    </button>
  );
}

export default function FitScoreHero() {
  const navigate = useNavigate();
  const [score, setScore] = useState(null);

  const refresh = () => {
    apiClient
      .get("/fitscore/today")
      .then((r) => setScore(r.data))
      .catch(() => setScore({ nutrition: 0, movement: 0, recovery: 0, hydration: 0, consistency: 0, total: 0, streak: 0 }));
  };

  useEffect(refresh, []);

  const total = score?.total ?? 0;
  const streak = score?.streak ?? 0;
  const consistencyPts = score?.consistency ?? 0;
  const streakActive = streak > 0;
  const action = nextAction(score);
  const NextIcon = action.Icon;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* ── Hero card (dark navy) ── */}
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        data-testid="fitscore-hero"
        style={{
          background: CARD_BG,
          border: `1px solid ${CARD_BORDER}`,
          borderRadius: 16,
          padding: 16,
          color: "#FFFFFF",
        }}
      >
        {/* Ring + copy */}
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <AnimatedRing score={total} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 data-testid="fitscore-status" style={{
              fontFamily: "Barlow Condensed, sans-serif", fontWeight: 800,
              fontSize: 22, lineHeight: 1.1, color: "#FFFFFF", margin: 0,
            }}>
              {statusLabel(total)}
            </h2>
            <p data-testid="fitscore-sub" style={{
              fontFamily: "Barlow, sans-serif", fontSize: 13,
              color: "rgba(255,255,255,0.65)", margin: "4px 0 10px 0",
            }}>
              {subLabel(score)}
            </p>
            <div data-testid="fitscore-streak-pill" style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "5px 12px", borderRadius: 100,
              background: streakActive ? "rgba(245,197,24,0.15)" : "rgba(245,197,24,0.10)",
              border: `1px solid ${streakActive ? "rgba(245,197,24,0.45)" : "rgba(245,197,24,0.30)"}`,
              color: "#F5C518", fontFamily: "Barlow, sans-serif", fontSize: 12, fontWeight: 700,
            }}>
              <Flame size={12} />
              <span data-testid="fitscore-streak-days">
                {streak} day{streak === 1 ? "" : "s"}
              </span>
              {/* The 5th component surfaced here: consistency points earned
                  from the current streak.  Same underlying data as the day
                  count; showing it prevents "invisible points". */}
              {consistencyPts > 0 && (
                <>
                  <span style={{ opacity: 0.55 }}>·</span>
                  <span data-testid="fitscore-streak-points">+{consistencyPts} pts</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Component tiles — Nutr / Move / Rest / H2O.
            Consistency (5th) doesn't get its own bar — it drives the streak pill above. */}
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))",
          gap: 14, marginTop: 18, paddingTop: 14,
          borderTop: "1px solid rgba(255,255,255,0.08)",
        }}>
          <ComponentTile Icon={Salad}    label="Nutr" value={score?.nutrition ?? 0} max={30} colour={TINTS.nutrition} testid="fitscore-tile-nutrition" onClick={() => navigate("/nutrition/scanner")} />
          <ComponentTile Icon={Dumbbell} label="Move" value={score?.movement ?? 0}  max={30} colour={TINTS.movement}  testid="fitscore-tile-movement"  onClick={() => navigate("/workouts")} />
          <ComponentTile Icon={Moon}     label="Rest" value={score?.recovery ?? score?.checkin ?? 0} max={20} colour={TINTS.recovery} testid="fitscore-tile-recovery" onClick={() => document.querySelector('[data-testid="morning-checkin-card"]')?.scrollIntoView({ behavior: "smooth", block: "center" })} />
          <ComponentTile Icon={Droplets} label="H₂O"  value={score?.hydration ?? 0} max={10} colour={TINTS.hydration} testid="fitscore-tile-hydration" onClick={() => navigate("/nutrition")} />
        </div>
      </motion.section>

      {/* ── Next Action card (white) ── */}
      <button
        type="button"
        onClick={() => navigate(action.to)}
        data-testid="fitscore-next-action"
        className="w-full flex items-center gap-3 text-left"
        style={{
          background: "#FFFFFF",
          border: "1px solid var(--border, rgba(12,12,11,0.08))",
          borderRadius: 16, padding: 14,
          boxShadow: "var(--shadow-soft)",
          cursor: "pointer",
        }}
      >
        <div style={{
          width: 40, height: 40, borderRadius: 12,
          background: `${action.tint}22`,
          display: "grid", placeItems: "center", flexShrink: 0,
        }}>
          <NextIcon size={20} color={action.tint} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: "Barlow Condensed, sans-serif", fontWeight: 700, fontSize: 16, color: "#0C0C0B" }}>
            {action.title}
          </div>
          <div style={{ fontFamily: "Barlow, sans-serif", fontSize: 12, color: "var(--text-secondary,#4A4845)", marginTop: 2 }}>
            {action.sub}
          </div>
        </div>
        <ChevronRight size={18} className="text-ink-tertiary" />
      </button>
    </div>
  );
}
